import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff, AlertCircle, Mail } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LoginPage = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', password: '' });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [unverifiedEmail, setUnverifiedEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMsg, setResendMsg] = useState('');

  const handleChange = e => {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
    setError('');
    setUnverifiedEmail('');
    setResendMsg('');
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.email || !form.password) {
      setError('Please enter your email and password.');
      return;
    }
    setLoading(true);
    try {
      const user = await login(form.email, form.password);
      const routes = {
        author: '/dashboard/author',
        reviewer: '/dashboard/reviewer',
        admin: '/dashboard/admin',
        superadmin: '/dashboard/superadmin',
      };
      navigate(routes[user.role] || '/dashboard');
    } catch (err) {
      const msg = err.message || '';
      if (msg.toLowerCase().includes('verify your email')) {
        setUnverifiedEmail(form.email);
        setError(msg);
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResendLoading(true);
    setResendMsg('');
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/auth/resend-verification?email=${encodeURIComponent(unverifiedEmail)}`,
        { method: 'POST' }
      );
      const data = await res.json();
      setResendMsg(data.message || 'Verification email sent.');
    } catch {
      setResendMsg('Something went wrong. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F7FA] flex items-center justify-center py-12 px-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <img src="/mah-logo.png" alt="MAH Quantum Research Institute" className="w-12 h-12 object-cover rounded" />
          </Link>
          <h1 className="font-serif text-3xl font-semibold text-[#1F1F1F] mb-1">Sign In</h1>
          <p className="text-[#6B7280] text-sm">MAH Quantum Research Institute Portal</p>
        </div>

        {/* Form */}
        <div className="bg-white border border-[#E5E7EB]">
          <div className="p-8">
            {error && (
              <div className="flex items-start gap-2.5 p-3 mb-4 bg-red-50 border border-red-200 text-red-700 text-sm">
                <AlertCircle size={15} className="flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            {unverifiedEmail && (
              <div className="mb-5 p-3 bg-amber-50 border border-amber-200 text-amber-800 text-sm">
                <div className="flex items-center gap-2 mb-2 font-medium">
                  <Mail size={14} />
                  Email not yet verified
                </div>
                {resendMsg ? (
                  <p className="text-sm text-amber-700">{resendMsg}</p>
                ) : (
                  <button
                    onClick={handleResend}
                    disabled={resendLoading}
                    className="text-[#1A73E8] font-medium hover:underline text-sm disabled:opacity-60"
                  >
                    {resendLoading ? 'Sending…' : 'Resend verification email →'}
                  </button>
                )}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-1.5">Email Address</label>
                <input
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="you@institution.edu"
                  className="w-full px-4 py-2.5 border border-[#E5E7EB] text-sm focus:ring-1 focus:ring-[#1A73E8] focus:border-[#1A73E8] focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#374151] mb-1.5">Password</label>
                <div className="relative">
                  <input
                    name="password"
                    type={showPass ? 'text' : 'password'}
                    value={form.password}
                    onChange={handleChange}
                    placeholder="••••••••"
                    className="w-full px-4 py-2.5 pr-10 border border-[#E5E7EB] text-sm focus:ring-1 focus:ring-[#1A73E8] focus:border-[#1A73E8] focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass(!showPass)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]"
                  >
                    {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-[#1A73E8] text-white font-semibold hover:bg-[#174EA6] disabled:opacity-60 transition-colors"
              >
                {loading ? 'Signing in…' : 'Sign In'}
              </button>
            </form>

            <p className="text-center text-sm text-[#6B7280] mt-5">
              Don't have an account?{' '}
              <Link to="/register" className="text-[#1A73E8] font-medium">
                Register as Author
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
